import { Routes } from '@angular/router';
import { HomeComponent } from './pages/home/home.component';
import { LoginComponent } from './pages/auth/login/login.component';
import { RegisterComponent } from './pages/auth/register/register.component';
import { ForgotPasswordComponent } from './pages/auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { MfaVerifyComponent } from './pages/auth/mfa-verify/mfa-verify.component';
import { GoogleCallbackComponent } from './pages/auth/google-callback/google-callback.component';
import { AuthShellComponent } from './layouts/auth-shell/auth-shell.component';
import { AppShellComponent } from './layouts/app-shell/app-shell.component';
import { AuthGuard, GuestGuard } from './guards/auth.guard';
import { AccountEntryGuard } from './guards/account-entry.guard';
import { OwnerGuard } from './guards/owner.guard';
import { AccountMenuPageComponent } from './pages/account/account-menu-page.component';
import { AccountSettingsLayoutComponent } from './pages/account/account-settings-layout.component';
import { ProfilePageComponent } from './pages/account/profile-page.component';
import { AddressesPageComponent } from './pages/account/addresses-page.component';
import { PaymentMethodsPageComponent } from './pages/account/payment-methods-page.component';
import { SecurityPageComponent } from './pages/account/security-page.component';
import { FeedbackPageComponent } from './pages/account/feedback-page.component';
import { DeleteAccountPageComponent } from './pages/account/delete-account-page.component';
import { WorkspacePageComponent } from './pages/workspace/workspace-page.component';
import { NotFoundPageComponent } from './pages/not-found/not-found-page.component';
import { BillingPlansPageComponent } from './pages/billing/billing-plans-page.component';
import { BillingCheckoutPageComponent } from './pages/billing/billing-checkout-page.component';
import { AdminUsersPageComponent } from './pages/admin/admin-users-page.component';
import { AdminPlansPageComponent } from './pages/admin/admin-plans-page.component';
import { AdminTicketsPageComponent } from './pages/admin/admin-tickets-page.component';
import { AdminAnalyticsPageComponent } from './pages/admin/admin-analytics-page.component';
import { ComponentBuilderPageComponent } from './pages/components/component-builder-page.component';
import { ComponentLibraryPageComponent } from './pages/components/component-library-page.component';
import { WebsitesPageComponent } from './pages/websites/websites-page.component';
import { WebsiteBuilderPageComponent } from './pages/websites/website-builder-page.component';
import { WebsitePreviewPageComponent } from './pages/websites/website-preview-page.component';
import { WebsiteTranslationsPageComponent } from './pages/websites/website-translations-page.component';
import { AiChatPageComponent } from './pages/ai/ai-chat-page.component';
import { AiUsagePageComponent } from './pages/ai/ai-usage-page.component';
import { pendingBuilderSaveGuard } from './guards/pending-builder-save.guard';

export const routes: Routes = [
  {
    path: 'login',
    component: AuthShellComponent,
    children: [
      {
        path: '',
        component: LoginComponent,
        pathMatch: 'full',
        canActivate: [GuestGuard]
      },
      {
        path: 'verify',
        component: MfaVerifyComponent,
        canActivate: [GuestGuard]
      }
    ]
  },
  {
    path: 'register',
    component: AuthShellComponent,
    children: [
      {
        path: '',
        component: RegisterComponent,
        canActivate: [GuestGuard]
      }
    ]
  },
  {
    path: 'auth/google/callback',
    component: AuthShellComponent,
    children: [
      {
        path: '',
        component: GoogleCallbackComponent,
        canActivate: [GuestGuard]
      }
    ]
  },
  {
    path: 'forgot-password',
    component: AuthShellComponent,
    children: [
      {
        path: '',
        component: ForgotPasswordComponent,
        canActivate: [GuestGuard]
      }
    ]
  },
  {
    path: 'reset-password',
    component: AuthShellComponent,
    children: [
      {
        path: '',
        component: ResetPasswordComponent
      }
    ]
  },
  {
    path: 'website-preview',
    component: WebsitePreviewPageComponent,
    canActivate: [AuthGuard]
  },
  {
    path: '',
    component: AppShellComponent,
    children: [
      {
        path: '',
        component: HomeComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'APP_NAME',
            translateTitle: true,
            showUserMenu: true
          }
        }
      },
      {
        path: 'websites/:websitePublicId',
        component: WebsitesPageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Websites',
            translateTitle: false,
            showUserMenu: true,
            fullWidth: true
          }
        }
      },
      {
        path: 'websites',
        component: WebsitesPageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Websites',
            translateTitle: false,
            showUserMenu: true,
            fullWidth: true
          }
        }
      },
      {
        path: 'website-builder',
        component: WebsiteBuilderPageComponent,
        canActivate: [AuthGuard],
        canDeactivate: [pendingBuilderSaveGuard],
        data: {
          shell: {
            title: 'Website builder',
            translateTitle: false,
            showUserMenu: true,
            fullWidth: true,
            rightPanel: {
              kind: 'website-builder-inspector',
              title: 'Page inspector',
              description: 'Edit the selected page node, including content, internal links, layout, styling, transforms, animation, and advanced CSS.'
            }
          }
        }
      },
      {
        path: 'website-translations',
        component: WebsiteTranslationsPageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Website translations',
            translateTitle: false,
            showUserMenu: true,
            fullWidth: true
          }
        }
      },
      {
        path: 'website-templates',
        component: WorkspacePageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Website templates',
            translateTitle: false,
            showUserMenu: true
          },
          workspacePage: {
            eyebrow: 'Template catalog',
            title: 'Website templates',
            description: 'Collect starter website blueprints, reusable page structures, and curated starting points for builders.',
            cards: [
              {
                title: 'Starter templates',
                description: 'Offer complete website baselines that can be cloned into a new project.'
              },
              {
                title: 'Vertical packs',
                description: 'Group templates by industry, sales funnel, service business, ecommerce, and portfolio use cases.'
              },
              {
                title: 'Template versioning',
                description: 'Keep template updates and migration strategy visible as the product matures.'
              }
            ],
            highlights: [
              'This area should be connected to both the website creation flow and the marketplace over time.',
              'A template should carry both layout structure and design-token defaults.'
            ]
          }
        }
      },
      {
        path: 'component-builder',
        component: ComponentBuilderPageComponent,
        canActivate: [AuthGuard],
        canDeactivate: [pendingBuilderSaveGuard],
        data: {
          shell: {
            title: 'Component builder',
            translateTitle: false,
            showUserMenu: true,
            fullWidth: true,
            rightPanel: {
              kind: 'component-builder-inspector',
              title: 'Component inspector',
              description: 'Edit the selected node, including layout, styling, transforms, animation, and advanced CSS.'
            }
          }
        }
      },
      {
        path: 'component-library',
        component: ComponentLibraryPageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Component library',
            translateTitle: false,
            showUserMenu: true,
            fullWidth: true
          }
        }
      },
      {
        path: 'component-marketplace',
        component: WorkspacePageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Component marketplace',
            translateTitle: false,
            showUserMenu: true
          },
          workspacePage: {
            eyebrow: 'Community exchange',
            title: 'Component marketplace',
            description: 'This area is reserved for discovering, evaluating, and importing components made by other builders.',
            cards: [
              {
                title: 'Discovery',
                description: 'Search, filter, and browse by layout role, popularity, price, and compatibility.'
              },
              {
                title: 'Trust signals',
                description: 'Expose preview quality, maintainer details, usage counts, and ratings before install.'
              },
              {
                title: 'Import pipeline',
                description: 'Handle licensing, updates, and ownership cleanly when a marketplace component enters a user library.'
              }
            ],
            highlights: [
              'This route should stay connected to the local component library instead of acting as a disconnected store page.'
            ]
          }
        }
      },
      {
        path: 'component-templates',
        component: WorkspacePageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Component templates',
            translateTitle: false,
            showUserMenu: true
          },
          workspacePage: {
            eyebrow: 'Reusable starters',
            title: 'Component templates',
            description: 'Reserve this area for starter component patterns that speed up creation without forcing builders into rigid presets.',
            cards: [
              {
                title: 'Pattern starters',
                description: 'Offer buttons, cards, hero blocks, forms, and layout primitives as configurable starting points.'
              },
              {
                title: 'Internal standards',
                description: 'Use templates to encode best practices while still allowing deep customization.'
              },
              {
                title: 'Conversion path',
                description: 'Templates created here should be convertible into library assets or marketplace-ready packages.'
              }
            ],
            highlights: [
              'This route should sit between raw component creation and polished reusable library assets.'
            ]
          }
        }
      },
      {
        path: 'ai/chat',
        component: AiChatPageComponent,
        canActivate: [AuthGuard],
        data: { shell: { title: 'BizzWeaver AI', translateTitle: false, showUserMenu: true, fullWidth: true } }
      },
      {
        path: 'ai/usage',
        component: AiUsagePageComponent,
        canActivate: [AuthGuard],
        data: { shell: { title: 'AI usage & credits', translateTitle: false, showUserMenu: true, fullWidth: true } }
      },
      {
        path: 'billing/plans',
        component: AccountSettingsLayoutComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Billing & plans',
            translateTitle: false,
            showUserMenu: true
          }
        },
        children: [
          {
            path: '',
            component: BillingPlansPageComponent
          }
        ]
      },
      {
        path: 'billing/checkout',
        component: BillingCheckoutPageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Checkout',
            translateTitle: false,
            showUserMenu: true,
            backLink: '/billing/plans',
            backLabel: 'Back to plans',
            translateBackLabel: false
          }
        }
      },
      {
        path: 'admin/users',
        component: AdminUsersPageComponent,
        canActivate: [AuthGuard, OwnerGuard],
        data: {
          shell: {
            title: 'Users & access',
            translateTitle: false,
            showUserMenu: true
          }
        }
      },
      {
        path: 'admin/plans',
        component: AdminPlansPageComponent,
        canActivate: [AuthGuard, OwnerGuard],
        data: {
          shell: {
            title: 'Plans & billing config',
            translateTitle: false,
            showUserMenu: true
          }
        }
      },
      {
        path: 'admin/tickets',
        component: AdminTicketsPageComponent,
        canActivate: [AuthGuard, OwnerGuard],
        data: {
          shell: {
            title: 'Feedback inbox',
            translateTitle: false,
            showUserMenu: true
          }
        }
      },
      {
        path: 'admin/analytics',
        component: AdminAnalyticsPageComponent,
        canActivate: [AuthGuard, OwnerGuard],
        data: {
          shell: {
            title: 'Product analytics',
            translateTitle: false,
            showUserMenu: true
          }
        }
      },
      {
        path: 'account',
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'User settings',
            translateTitle: false,
            showUserMenu: true,
            backLink: '/',
            backLabel: 'Back',
            translateBackLabel: false
          }
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: AccountMenuPageComponent,
            canActivate: [AccountEntryGuard]
          },
          {
            path: '',
            component: AccountSettingsLayoutComponent,
            children: [
              {
                path: 'profile',
                component: ProfilePageComponent
              },
              {
                path: 'addresses',
                component: AddressesPageComponent
              },
              {
                path: 'payment-methods',
                component: PaymentMethodsPageComponent
              },
              {
                path: 'security',
                component: SecurityPageComponent
              },
              {
                path: 'feedback',
                component: FeedbackPageComponent
              },
              {
                path: 'danger-zone',
                component: DeleteAccountPageComponent
              },
              {
                path: 'delete-account',
                pathMatch: 'full',
                redirectTo: 'danger-zone'
              }
            ]
          }
        ]
      },
      {
        path: '**',
        component: NotFoundPageComponent,
        canActivate: [AuthGuard],
        data: {
          shell: {
            title: 'Not found',
            translateTitle: false,
            showUserMenu: true
          }
        }
      }
    ]
  },
  {
    path: '**',
    component: AuthShellComponent,
    children: [
      {
        path: '',
        component: NotFoundPageComponent
      }
    ]
  }
];
